/**
 * The rules of Wild21, as pure functions.
 *
 * Ported line-for-line in behaviour from the original React Native app
 * (`src/screens/GameBoard/tableau.js` → `check()`), including the parts that
 * look like accidents but ship in the real game:
 *
 * - `soft` counts only the FIRST ace as 11; every later ace counts 1.
 * - The 5-card bonus is evaluated independently of the 21 bonus, so a fifth
 *   card that lands on exactly 21 pays both.
 * - A wild (J♠ / J♣) always clears the pile, always cancels the bust, and when
 *   it is the fifth card it pays the 5-card bonus *even if the pile is over 21*.
 *
 * Nothing here holds state. `resolvePile` is the single source of truth for what
 * one card landing on one pile means.
 */

import {
  POINTS,
  WILD_CARDS,
  type BonusKind,
  type CardId,
  type PileTotals,
  type Rank,
  type Suit,
} from '@/game/types';

/** Cards in a pile that trigger the 5-card bonus. */
export const FIVE_CARD_COUNT = 5;

/** The number every pile is racing toward. */
export const TARGET = 21;

const RANK_VALUE: Readonly<Record<Rank, number>> = {
  a: 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10,
  j: 10,
  q: 10,
  k: 10,
};

const SUIT_SET: ReadonlySet<string> = new Set<Suit>(['c', 'd', 'h', 's']);
const WILD_SET: ReadonlySet<string> = new Set<CardId>(WILD_CARDS);

function isSuit(value: string): value is Suit {
  return SUIT_SET.has(value);
}

function isRank(value: string): value is Rank {
  return Object.prototype.hasOwnProperty.call(RANK_VALUE, value);
}

/** Split a card id such as `10d` into its rank and suit. Throws on nonsense. */
export function parseCard(card: CardId): { rank: Rank; suit: Suit } {
  const suit = card.slice(-1);
  const rank = card.slice(0, -1);
  if (!isSuit(suit) || !isRank(rank)) {
    throw new Error(`Unknown card id: "${card}"`);
  }
  return { rank, suit };
}

export function cardSuit(card: CardId): Suit {
  return parseCard(card).suit;
}

export function cardRank(card: CardId): Rank {
  return parseCard(card).rank;
}

/** Face value: ace = 1 (the soft 11 is applied by `totalsOf`), J/Q/K = 10. */
export function cardValue(card: CardId): number {
  return RANK_VALUE[parseCard(card).rank];
}

export function isAce(card: CardId): boolean {
  return cardRank(card) === 'a';
}

/** J♠ and J♣ only. Every other jack is an ordinary 10. */
export function isWildCard(card: CardId): boolean {
  return WILD_SET.has(card);
}

export function hasWild(cards: readonly CardId[]): boolean {
  return cards.some(isWildCard);
}

/**
 * Both running totals for a pile.
 * `hard` counts every ace as 1. `soft` promotes only the first ace to 11.
 */
export function totalsOf(cards: readonly CardId[]): PileTotals {
  let hard = 0;
  let soft = 0;
  let sawAce = false;
  for (const card of cards) {
    const value = cardValue(card);
    hard += value;
    soft += value === 1 && !sawAce ? 11 : value;
    if (value === 1) sawAce = true;
  }
  return { hard, soft };
}

/** Either total landing exactly on 21 pays the 21 bonus. */
export function isTwentyOne(totals: PileTotals): boolean {
  return totals.hard === TARGET || totals.soft === TARGET;
}

/** A pile only busts when *both* totals are over 21 — and never with a wild. */
export function isOver(totals: PileTotals): boolean {
  return totals.hard > TARGET && totals.soft > TARGET;
}

/** The best total that is still legal, or the hard total when both are bust. */
export function bestTotal(totals: PileTotals): number {
  if (totals.soft <= TARGET) return Math.max(totals.hard, totals.soft);
  return totals.hard;
}

/** What one card landing on one pile does, before any scoring is applied. */
export interface PileOutcome {
  /** Pile contents including the new card, at the moment of resolution. */
  cards: CardId[];
  totals: PileTotals;
  /** True when the pile empties as a result of this card. */
  cleared: boolean;
  is21: boolean;
  isFiveCard: boolean;
  isWild: boolean;
  isBust: boolean;
  /** Bonuses earned, in display order: 21, 5-card, wild. */
  bonuses: BonusKind[];
  /** Points from `bonuses`, streak excluded. */
  basePoints: number;
}

const BONUS_POINTS: Readonly<Record<BonusKind, number>> = {
  twentyOne: POINTS.twentyOne,
  fiveCard: POINTS.fiveCard,
  wild: POINTS.wild,
};

/** Points for a set of bonuses, in isolation from any streak. */
export function bonusPoints(bonuses: readonly BonusKind[]): number {
  return bonuses.reduce((sum, bonus) => sum + BONUS_POINTS[bonus], 0);
}

/**
 * Drop `card` onto `pile` and work out what happens.
 * `pile` is not mutated; the caller applies `cleared`.
 */
export function resolvePile(pile: readonly CardId[], card: CardId): PileOutcome {
  const cards = [...pile, card];
  const totals = totalsOf(cards);
  const wild = hasWild(cards);

  const is21 = isTwentyOne(totals);
  // The original evaluates the 5-card bonus independently of 21, so a fifth
  // card landing on 21 pays 400 + 600.
  let isFiveCard =
    cards.length === FIVE_CARD_COUNT && (totals.hard <= TARGET || totals.soft <= TARGET);
  let isBust = isOver(totals);

  if (wild) {
    // The wild is a rescue card: it cancels the bust and, as the fifth card,
    // still pays the 5-card bonus even though the pile is over 21.
    isBust = false;
    if (cards.length === FIVE_CARD_COUNT) isFiveCard = true;
  }

  const bonuses: BonusKind[] = [];
  if (is21) bonuses.push('twentyOne');
  if (isFiveCard) bonuses.push('fiveCard');
  if (wild) bonuses.push('wild');

  return {
    cards,
    totals,
    cleared: is21 || isFiveCard || wild || isBust,
    is21,
    isFiveCard,
    isWild: wild,
    isBust,
    bonuses,
    basePoints: bonusPoints(bonuses),
  };
}
